import { useMutation, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { useState } from 'react';
import styled from 'styled-components';
import { Alert, AlertVariant } from '../../components/alert/alert';
import { Button } from '../../components/button/button';
import { Container } from '../../components/container/container';
import { FieldGroup } from '../../components/field-group/field-group';
import { Heading } from '../../components/heading/heading';
import { Input } from '../../components/input/input';
import { LabelInput } from '../../components/input/label-input';
import { LeaderboardTable } from '../../components/table/leaderboard-table';
import { useAllUsersQuery, useMeQuery } from '../../graphql/types';
import { color } from '../../styles/mixins';
import { label12, typography } from '../../styles/typography';

const Center = styled.div`
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;

  ${typography.fonts.mercury(32)}
  color: ${color('gray80')};
`;

const Label = styled.label`
  display: block;
  ${label12};
  line-height: 18px;
  color: ${color('gray80')};
`;

const ScrollTh = styled.th`
  text-align: left;
  font-weight: 500;
  position: sticky;
  background-color: white;
  top: 0px;
  z-index: 10;
`;

const UserTable = styled.table`
  tbody tr:nth-child(even) td {
    background-color: ${color('gray20')};
  }
  tbody td,
  thead th {
    padding: 4px;
  }
`;

function AllUsers() {
  const allUsers = useAllUsersQuery();

  return (
    <FieldGroup title="All users">
      <div style={{ maxHeight: 500, overflow: 'scroll' }}>
        <UserTable>
          <thead>
            <tr>
              <ScrollTh>Name</ScrollTh>
              <ScrollTh
                style={{
                  minWidth: 200,
                }}
              >
                Email
              </ScrollTh>
              <ScrollTh
                style={{
                  minWidth: 300,
                }}
              >
                User No
              </ScrollTh>
              <ScrollTh
                style={{
                  minWidth: 120,
                }}
              >
                Account No
              </ScrollTh>
              <ScrollTh
                style={{
                  minWidth: 120,
                }}
              >
                Paddle No
              </ScrollTh>
              <ScrollTh
                style={{
                  minWidth: 200,
                }}
              >
                Signed up
              </ScrollTh>
              <ScrollTh
                style={{
                  minWidth: 200,
                }}
              >
                License agreed at
              </ScrollTh>
              <ScrollTh
                style={{
                  minWidth: 200,
                }}
              >
                Paddle Created
              </ScrollTh>
              <ScrollTh
                style={{
                  minWidth: 120,
                }}
              >
                Share Data
              </ScrollTh>
            </tr>
          </thead>
          <tbody>
            {allUsers.data?.allUsers.map((user) => (
              <tr key={user.id}>
                <td style={{ whiteSpace: 'pre' }}>{user.name}</td>
                <td>{user.emailAddress}</td>
                <td>{user.externalUserId}</td>
                <td>{user.externalAccountId}</td>
                <td>
                  {user.paddles?.[0]?.number
                    ? `#${user.paddles?.[0]?.number}`
                    : '-'}
                </td>
                <td>{new Date(user.createdAt).toLocaleString()}</td>
                <td>{new Date(user.licenseAgreedAt).toLocaleString()}</td>
                <td>
                  {user.paddles?.[0]?.createdAt
                    ? new Date(user.paddles?.[0]?.createdAt).toLocaleString()
                    : '-'}
                </td>

                <td>
                  <span title="Allow opportunities with CNV and Vintner">
                    {user.shareWineOpportunities ? '☑' : '☐'}{' '}
                  </span>
                  <span title="Allow Promotions with CNV">
                    {user.shareDataNVV ? '☑' : '☐'}
                  </span>{' '}
                  <span title="Allow Promotions with Vintner">
                    {user.shareDataVintner ? '☑' : '☐'}
                  </span>{' '}
                </td>
              </tr>
            ))}
          </tbody>
        </UserTable>
      </div>
    </FieldGroup>
  );
}

function PlaceBid() {
  const [lotNo, setLotNo] = useState('');
  const [paddleNo, setPaddleNo] = useState('');
  const [amount, setAmount] = useState(0);
  const [placeBidMutation, { loading }] = useMutation(gql`
    mutation ($lotNumber: Int!, $paddleNumber: Int!, $amount: Int!) {
      createBid(
        amount: $amount
        lotNumber: $lotNumber
        paddleNumber: $paddleNumber
      ) {
        id
        amount
      }
    }
  `);
  return (
    <FieldGroup title="Place Bid">
      <div>
        <Label htmlFor="bid_lot_no">Lot #:</Label>
        <Input
          placeholder="Lot Number"
          id="bid_lot_no"
          value={lotNo}
          onChange={(e) => setLotNo(e.currentTarget.value)}
        />
      </div>
      <div>
        <Label htmlFor="bid_paddle_no">Paddle #:</Label>
        <Input
          placeholder="Paddle Number"
          id="bid_paddle_no"
          value={paddleNo}
          onChange={(e) => setPaddleNo(e.currentTarget.value)}
        />
      </div>
      <LabelInput
        id="bid_amount"
        label="Amount"
        value={amount}
        onValueChange={(values) => {
          const { value } = values;
          setAmount(Number(value));
        }}
      />
      <Button
        style={{ marginBottom: 16 }}
        onClick={() => {
          placeBidMutation({
            variables: {
              amount,
              lotNumber: Number(lotNo),
              paddleNumber: Number(paddleNo),
            },
            onCompleted(data) {
              alert('Bid has been added');
              setPaddleNo('');
              setLotNo('');
              setAmount(0);
            },
            onError(error) {
              alert(error.graphQLErrors?.[0]?.message);
            },
          });
        }}
        loading={loading}
      >
        Submit Bid
      </Button>
    </FieldGroup>
  );
}

function RemoveBid() {
  const [lotNo, setLotNo] = useState('');
  const [paddleNo, setPaddleNo] = useState('');
  const [amount, setAmount] = useState(0);
  const [removeBidMutation, { loading, data, error }] = useMutation(gql`
    mutation ($lotNumber: Int!, $paddleNumber: Int!, $amount: Int!) {
      removeBid(
        amount: $amount
        lotNumber: $lotNumber
        paddleNumber: $paddleNumber
      )
    }
  `);
  return (
    <FieldGroup title="Remove Bid">
      <Alert
        copy="WARNING! This will remove a bid from the lot"
        variant={AlertVariant.Danger}
      />
      <div>
        <Label htmlFor="rmbid_lot_no">Lot #:</Label>
        <Input
          placeholder="Lot Number"
          id="rmbid_lot_no"
          value={lotNo}
          onChange={(e) => setLotNo(e.currentTarget.value)}
        />
      </div>
      <div>
        <Label htmlFor="rmbid_paddle_no">Paddle #:</Label>
        <Input
          placeholder="Paddle Number"
          id="rmbid_paddle_no"
          value={paddleNo}
          onChange={(e) => setPaddleNo(e.currentTarget.value)}
        />
      </div>
      <LabelInput
        id="rmbid_amount"
        label="Amount"
        value={amount}
        onValueChange={(values) => {
          const { value } = values;
          setAmount(Number(value));
        }}
      />
      <Button
        style={{ marginBottom: 16 }}
        onClick={() => {
          // eslint-disable-next-line no-restricted-globals
          const sure = confirm('Are you sure?');
          if (sure) {
            removeBidMutation({
              variables: {
                amount,
                lotNumber: Number(lotNo),
                paddleNumber: Number(paddleNo),
              },
              onCompleted(data) {
                alert('Bid has been removed');
                setPaddleNo('');
                setLotNo('');
                setAmount(0);
              },
              onError(error) {
                alert(error.graphQLErrors?.[0]?.message);
              },
            });
          }
        }}
        loading={!error && !data && loading}
        variant="secondary"
      >
        Remove Bid
      </Button>
    </FieldGroup>
  );
}

function ExportData() {
  const [exportUsersAndBids, { data, loading }] = useMutation(gql`
    mutation {
      exportAuction
    }
  `);
  return (
    <FieldGroup title="Export bid data">
      {data?.exportAuction ? (
        <Button
          as="a"
          download={`bid_export_${Date.now()}.zip`}
          href={`data:application/zip;base64,${data.exportAuction}`}
        >
          Download export
        </Button>
      ) : (
        <Button
          style={{ marginBottom: 16 }}
          onClick={() => exportUsersAndBids()}
          loading={loading}
        >
          Create export
        </Button>
      )}
    </FieldGroup>
  );
}

function ExportAuctionsAndLotsData() {
  const [exportAuctionsAndLots, { data, loading }] = useMutation(gql`
    mutation {
      exportAuctionsAndLots
    }
  `);
  return (
    <FieldGroup title="Export auctions and lots data">
      {data?.exportAuctionsAndLots ? (
        <Button
          as="a"
          download={`auction_and_lots_export_${Date.now()}.zip`}
          href={`data:application/zip;base64,${data.exportAuctionsAndLots}`}
        >
          Download export
        </Button>
      ) : (
        <Button
          style={{ marginBottom: 16 }}
          onClick={() => exportAuctionsAndLots()}
          loading={loading}
        >
          Create export
        </Button>
      )}
    </FieldGroup>
  );
}

function Stats() {
  const stats = useQuery(gql`
    {
      stats {
        lotsCount
        bidsCount
        paddleCount
        lotsEndedCount
      }
    }
  `);
  return (
    <FieldGroup title="Stats">
      {stats.loading ? (
        'Loading stats...'
      ) : stats.data ? (
        <LeaderboardTable>
          <thead>
            <tr>
              <th>Field</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Total Lots</td>
              <td>{stats.data?.stats?.lotsCount}</td>
            </tr>
            <tr>
              <td>Total Bids</td>
              <td>{stats.data?.stats?.bidsCount}</td>
            </tr>
            <tr>
              <td>Total Paddles</td>
              <td>{stats.data?.stats?.paddleCount}</td>
            </tr>
            <tr>
              <td>Ended lots</td>
              <td>
                {stats.data?.stats?.lotsEndedCount} (
                {(
                  (stats.data?.stats?.lotsEndedCount /
                    stats.data?.stats?.lotsCount) *
                  100
                )?.toFixed(2) ?? 0}
                %)
              </td>
            </tr>
          </tbody>
        </LeaderboardTable>
      ) : (
        <>No data</>
      )}
    </FieldGroup>
  );
}

export function Admin() {
  const me = useMeQuery({
    fetchPolicy: 'cache-only',
  });

  if (me.loading || !me.data) {
    return <Center>Loading...</Center>;
  }

  if (me.data && !me.data?.me?.isAdmin) {
    return <Center>No admin</Center>;
  }

  return (
    <Container>
      <div style={{ maxWidth: 800, width: '100%' }}>
        <Heading style={{ marginBottom: 20 }}>Admin system</Heading>
        <Stats />
        <AllUsers />
        <PlaceBid />
        <RemoveBid />
        <ExportData />
        <ExportAuctionsAndLotsData />
      </div>
    </Container>
  );
}
